<template>
    <div>

        <vue-headful title="Incident/Non-Compliant Correlation :: MeritEHS"/>

        <h4 class="py-3 mb-4 d-print-none">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item active">Incident/Non-Compliant Correlation</li>
            </ol>
        </h4>

        <div :class="pdf_view ? 'container' : ''" :style="pdf_view ? 'max-width: 980px;' : ''">
            <div class="row d-none d-print-block mb-4" v-if="pdfLogo" style="margin-bottom: -20px; z-index: 100;">
                <div class="col-md-3">
                    <img :src="pdfLogo" class="img-fluid" />
                </div>
            </div>

            <div class="alert alert-info d-print-none" v-if="pdf_view">
                <strong>How to Export as PDF:</strong>
                <ol>
                    <li>On your browser, there should be a "File" or "Options" tab with a "Print" option. You may also hold down the "Ctrl" key and press the "P" key.</li>
                    <li>This will bring up the print page options box. There should be an option called "Select Printer" or "Destination".</li>
                    <li>Select "Save as PDF" or "Adobe PDF".</li>
                    <li>Click "Save" or "Print". This will open your file explorer and let you save it to your computer as a PDF.</li>
                </ol>
            </div>

            <div class="card">
                <div class="card-body">

                    <div class="d-print-none mb-4">
                        <button class="btn btn-sm btn-default" v-on:click="show_filters = !show_filters" title="Show/Hide Filters"><i class="fa fa-filter"></i>
                            <span class="badge" style="background-color: #d9534f" :title="`${filterCount} Filter(s) Applied`" v-if="filterCount > 0">{{ filterCount }}</span>
                        </button>

                        Correlation Filters:
                        <div v-show="show_filters" class="mt-3">
                            <div class="d-inline-block">
                                <multi-select v-model="filters['interval']" :btnLabel="(values) => ('Interval' + (values.length > 0 ? ' - ' + values[0].name : ''))" :selectOptions="intervalOptions" :options="{multi: false}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="filters['incident_types']" :btnLabel="(values) => ('Incident Types ('+values.length+')')" :selectOptions="incident_types" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="filters['inspection_forms']" :btnLabel="(values) => ('Inspection Forms ('+values.length+')')" :selectOptions="inspection_forms" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="filters['jobsite.JobsiteId']" :btnLabel="(values) => ('Jobsites ('+values.length+')')" :selectOptions="jobsites" :options="{multi: true}" :search="true"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="filters['divisions']" :btnLabel="(values) => ('Divisions ('+values.length+')')" :selectOptions="divisions" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <date-range-selector ref="inspDate" buttonTitle="Date" v-model="filters['date_range']" />
                            </div>

                            <div style="margin-top: 20px;">
                                <button type="button" class="btn btn-sm btn-default" v-on:click="closeFilters()">
                                    <i class="fa fa-caret-up"></i> Close
                                </button>
                                <button type="button" class="btn btn-sm btn-warning" v-on:click="clearFilters()">
                                    <i class="fa fa-times"></i> Clear
                                </button>
                                <button type="button" class="btn btn-sm btn-success" v-on:click="applyFilters()">
                                    <i class="fa fa-filter"></i> Apply
                                </button>
                                <div class="d-inline-block ml-2">
                                    <b-form-checkbox v-model="pdf_view" :value="true" :unchecked-value="false" plain class="ml-2">PDF View</b-form-checkbox>
                                </div>
                                <div class="d-inline-block text-danger ml-2" v-if="show_empty_data_msg">
                                    <i class="fa fa-exclamation-circle"></i> Select at least 1 Incident Type and 1 Inspection Form
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="loading">
                        <i class="fa fa-spin fa-spinner"></i> Initializing
                    </div>
                    <div v-else>
                        <highcharts id="highcharts-canvas" :options="options" style="min-height: 800px;" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import filterExtractor from '@/helpers/TableFilterExtractor';
    import moment from 'moment-timezone';

    export default {
        data() {
            return {
                series: [],
                labels: [],
                loading: false,
                show_filters: true,
                show_empty_data_msg: false,
                filterCount: 0,
                pdf_view: false,
                pdfLogo: this.$store.state.user.pdf_logo,

                intervalOptions: [
                    {id: 'Week', name: 'Weekly'},
                    {id: 'Month', name: 'Monthly'},
                    {id: 'Year', name: 'Yearly'}
                ],

                filters: {
                    'interval': [{'name': 'Monthly', 'id': 'Month'}],
                    'inspection_forms': [],
                    'incident_types': [],
                    'divisions': [],
                    'jobsite.JobsiteId': [],
                    'date_range': 'ytd'
                },

                dateLabel: null,

                incident_types: [],
                inspection_forms: [],
                jobsites: [],
                divisions: [],
            }
        },

        watch: {
            pdf_view(newVal, oldVal){
                //Helper?
                this.loading = true;

                //Redraw the Draw
                this.getData();
            },

            'filters.date_range'(newVal, oldVal){
                //Set the label
                this.setDateLabel();
            },
        },

        computed: {
            today(){
                //Send todays date
                return moment().format("YYYY-MM-DD");
            },

            options(){
                return {
                    chart: {
                        type: 'line'
                    },
                    title: {
                        text: 'Incident/Non-Compliant Correlation: ' + this.dateLabel,
                    },
                    xAxis: {
                        categories: this.labels
                    },
                    yAxis: {
                        title: {
                            text: '# of Incidents/Non-Compliant Items'
                        },
                        tickInterval: 1,
                        //tickPixelInterval: 20
                    },
                    plotOptions: {
                        bar: {
                            dataLabels: {
                                enabled: true,
                                formatter: function(){
                                    return this.point.series.name+': '+this.point.y;
                                }
                            }
                        }
                    },
                    legend: {
                        layout: 'horizontal',
                        align: 'right',
                        verticalAlign: 'bottom',
                        x: 0,
                        y: 0,
                        floating: false,
                        borderColor: '#dddddd',
                        borderWidth: 1,
                    },
                    series: this.series,
                    tooltip: {
                        formatter: function() {
                            //Send it
                            var html = 'Type: <b>'+ this.series.name + '</b><br/>' +
                                    'Date: <b>'+ this.x + '</b><br/>' +
                                    'Total: <b>'+ this.y + '</b><br/>';

                            return html;
                        }
                    },
                }
            },
        },

        methods: {
            setDateLabel(){
                //Set the Label
                this.dateLabel = this.$refs.inspDate.getLabel();
            },

            applyFilters() {
                //Update the count
                this.updateFilterCount();

                //Get the data
                this.getData();
            },

            closeFilters() {
                //Hide the modal
                this.show_filters = false;
            },

            updateFilterCount() {
                //Reset the count
                this.filterCount = 0;
                var status = false;

                // Loop over objects in this.filters
                for (var obj in this.filters) {

                    if (this.filters[obj] == null || this.filters[obj] === '') {
                        continue;
                    }

                    if (Array.isArray(this.filters[obj])) {
                        if (this.filters[obj].length > 0) {
                            this.filterCount++;
                        }
                        continue;
                    }

                    if (typeof this.filters[obj] === 'object') {
                        var Empty = true;
                        for (var key in this.filters[obj]) {
                            if (this.filters[obj].hasOwnProperty(key));
                            Empty = false;
                        }
                        if (!Empty) {
                            this.filterCount++;
                        }
                        continue;
                    }

                    if (typeof this.filters[obj] === 'string' || typeof this.filters[obj] === 'number') {
                        this.filterCount++;
                    }
                }
            },

            getData() {
                //SEtup
                var filters = filterExtractor.extractFilters(this.filters);

                //If Incident Type AND Inspection Form are empty, stop
                if(filters.incident_types.length === 0 || filters.inspection_forms.length === 0)
                {
                    //Stop here
                    this.$toastr.warning('Select at least 1 Incident Type and 1 Inspection Form');

                    //Show the Error Warning
                    this.show_empty_data_msg = true;

                    //Setop
                    this.loading = false;

                    //Stop
                    return;
                }

                //Set helper to false
                this.show_empty_data_msg = false;

                this.$http
                    .get('reports/correlation-stats', filters)
                    .then(response => {
                        if(response.data.series.length === 0)
                        {
                            //Alert the user
                            this.$toastr.warning('There is no data for your currently selected filters.')

                            //Stop here
                            return;
                        }

                        //Set all the data
                        this.labels = response.data.labels;
                        this.series = response.data.series;
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;
                    });
            },

            getFilters() {

                //Send the response
                this.$http
                    .get('reports/filters/correlation', this.filters)
                    .then(response => {
                        //Set all the data
                        this.jobsites = response.data.jobsites;
                        this.divisions = response.data.divisions;
                        this.inspection_forms = response.data.inspection_forms;
                        this.incident_types = response.data.incident_types;

                        //Set loading
                        this.loading = false;
                    })
                    .catch(response => {

                    })
                    .then(() => {

                    });
            },
        },

        mounted() {
            //Set the Dat Label
            this.setDateLabel();
            //this.getData();

            //Get the Filters
            this.getFilters();
        }
    }
</script>

<style type="text/css" scoped>
    @media print{
        /*@page {
            size: landscape;
        }*/

        #highcharts-canvas {
            width: 2300;
        }

        .card {
            background-color: transparent;
            border: none;
        }
    }
</style>
